/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import LOCAL_STORAGE_KEYS, {
  SESSION_STORAGE_KEYS,
} from "constants/STORAGE_KEYS";
import useFetcher from "hooks/useFetcher";
import { getEvents } from "lib/eventsApi";
import { hasUnSeenNotification } from "lib/notificationsApi";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import useEventRouting from "hooks/useEventRouting";
import { useLocation } from "react-router-dom";
import extractToken from "utils/extractToken";
import useStateRef from "react-usestateref";
import ROUTES from "routes";
import useLocalStorage from "utils/useLocalStorage";

interface IGeneralContext {
  events: any[];
  isFirstRender: boolean;
  setIsFirstRender: (isFirstRender: boolean) => void;
  setEvents: (events: any[] | Function) => void;
  hasNotifications: boolean;
  setHasNotifications: (hasNotifications: boolean) => void;
  setValidRoutes: (value: string) => void;
  validRoutes: string[];
  fetchEvents: () => void;
  isLoggedIn: boolean;
  setIsLoggedIn: (isLoggedIn: boolean) => void;
}

const initialData: IGeneralContext = {
  events: [],
  setEvents: () => { },
  isFirstRender: true,
  setIsFirstRender: () => { },
  hasNotifications: false,
  setHasNotifications: () => { },
  setValidRoutes: () => { },
  validRoutes: [],
  fetchEvents: () => { },
  isLoggedIn: false,
  setIsLoggedIn: () => { },
};

const GeneralContext = createContext<IGeneralContext>(initialData);

const FIVE_MINUTES = 1000 * 60 * 5;

export const GeneralProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [localIsLoggedIn, setIsLoggedIn] = useState(false);
  const [token] = useLocalStorage(LOCAL_STORAGE_KEYS.token, null);
  const [id] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);

  // Two cases where the stored credentials must NOT be treated as a live
  // session, both evaluated at render time because getEvents starts on mount —
  // any fix applied later in an effect is already too late, and its 401 clears
  // storage and hard-redirects to "/", tearing down whatever screen is loading:
  //
  // 1. A magic-link token in the URL: authentication is about to be redone from
  //    scratch, and anything still in storage is a previous session — dead
  //    server-side once the account has been reset.
  // 2. A held step token: the account owes a password change, so the backend
  //    401s every dashboard endpoint until the Create New Password screen is
  //    completed.
  const { search } = useLocation();
  const isAuthenticatingViaMagicLink = extractToken(search).JWToken.length > 0;
  const hasStepToken = !!sessionStorage.getItem(SESSION_STORAGE_KEYS.stepToken);
  const hasSession =
    !!token && !!id && !hasStepToken && !isAuthenticatingViaMagicLink;
  const isLoggedIn = hasSession && !!localIsLoggedIn;

  const { pathname, isCurrentPage, eventId } = useEventRouting();
  const {
    data: events = [] as any[],
    mutate: updateEvents,
    refetch: fetchEvents,
  } = useFetcher(getEvents, {
    defaultState: [],
    startOnMount: hasSession,
    refetchOnConditions: [hasSession],
    enabled: hasSession,
    onSuccess: (data) => {
      if (data) {
        const eventNamePerEventId = data.reduce(
          (acc: any, event: any) => {
            acc[event?.id] = event?.title;
            return acc;
          },
          {} as Record<string, string>,
        );
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.eventNamePerEventId,
          JSON.stringify(eventNamePerEventId),
        );
      }
    },
  });
  const _ = useFetcher(() => hasUnSeenNotification(eventId), {
    key: `notifications-${eventId}`,
    interval: FIVE_MINUTES,
    defaultState: {} as { unseen_notifications: boolean },
    startOnMount: false,
    refetchOnConditions: [isLoggedIn, pathname],
    enabled:
      isCurrentPage(ROUTES.dashboard.base, { exact: false }) &&
      isLoggedIn &&
      !!eventId,
    onSuccess: (data) => {
      setHasNotifications(!!data?.unseen_notifications);
    },
  });
  const [, setIsFirstRender, isFirstRenderRef] = useStateRef(true);
  const [hasNotifications, setHasNotifications] = useState(false);
  const [visitedRoutes, setVisitedRoutes] = useState<string[]>([]);
  const validRoutesRef = useRef<string[]>([]);
  const setValidRoutes = (value: string) => {
    validRoutesRef.current.push(value);
  };

  useEffect(() => {
    if (visitedRoutes.length > 0) {
      setIsFirstRender(false);
    }
    if (!visitedRoutes.includes(pathname)) {
      setVisitedRoutes([...visitedRoutes, pathname]);
    }
  }, [pathname]);
  const ctx = {
    setIsFirstRender,
    isFirstRender: isFirstRenderRef.current,
    events,
    setEvents: (newEvents: any[] | Function) => {
      if (typeof newEvents === "function") {
        const data = newEvents(events);
        updateEvents(data);
      } else {
        updateEvents(newEvents);
      }
    },
    fetchEvents,
    hasNotifications,
    setHasNotifications,
    setValidRoutes,
    validRoutes: validRoutesRef.current,
    isLoggedIn,
    setIsLoggedIn,
  };
  return (
    // @ts-ignore
    <GeneralContext.Provider value={ctx}>{children}</GeneralContext.Provider>
  );
};

export const useGlobal = () => useContext(GeneralContext);
